import React from 'react';
import { BagCheck, BagPlus, Files, Pencil, Trash } from 'react-bootstrap-icons';
import BootstrapTable, { SelectRowProps } from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import * as Rx from "rxjs";
import moment from 'moment';

import { OrderItem, OrderState } from '../../providers/Models';
import { DragAndDrop } from '../Common/DragAndDrop';
import ReactTooltip from 'react-tooltip';

interface Props {
  selectOrderHandler: (orderId: number) => void,
  addOrderHandler: () => void,
  modifyOrderHandler: (orderId: number) => void,
  saveOrderHandler: (orderId: number) => void,
  removeOrderHandler: (orderId: number) => void,
  copyOrderHandler: (orderId: number) => void,
  dropFileHandler: (files: any) => void,
  orderListSubject: Rx.BehaviorSubject<OrderItem[]>,
  selectionSubject: Rx.BehaviorSubject<number>,
}

interface State {
  orders: OrderItem[],
  selected: number[],
}

export class OrderList extends React.Component<Props, State> {

  node: any;
  orderListSubscription?: Rx.Subscription;
  selectionSubscription?: Rx.Subscription;

  constructor(props: Props) {
    super(props);
    this.state = { orders: [], selected: [] };
  }

  componentDidMount() {
    this.orderListSubscription = this.props.orderListSubject.subscribe((data) => {
      this.setState({ orders: data });
    });
    this.selectionSubscription = this.props.selectionSubject.subscribe((data) => {
      this.setState({ selected: [data] });
    });
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    const selectionChanged = prevState.selected[0] !== this.state.selected[0];
    const ordersChanged = prevState.orders !== this.state.orders;

    if (selectionChanged || ordersChanged) {
      const index = this.state.orders.findIndex(o => o.id === this.state.selected[0]);
      const indexPage = Math.ceil((index + 1) / this.node.paginationContext.currSizePerPage);
      if (index > -1 && indexPage !== this.node.paginationContext.currPage) {
        this.node.paginationContext.currPage = indexPage;
        this.forceUpdate();
      }
    }
  }

  componentWillUnmount() {
    this.orderListSubscription?.unsubscribe();
    this.selectionSubscription?.unsubscribe();
  }

  actionFormatter = (_cell: any, row: OrderItem, _rowIndex: any, _extra: any) => {
    const hasOrderDetails = row.orderDetailCount > 0;

    if (row.state === 0) {
      return (
        <div className="d-flex justify-content-center">
          <button type="button" className="icon-button" aria-label="Bestellung bearbeiten" data-tip data-for="modifyOrderTip" onClick={() => this.props.modifyOrderHandler(row.id)}><Pencil /></button>
          {hasOrderDetails && <button type="button" className="icon-button ml-2" aria-label="Bestellung abschliessen" data-tip data-for="saveOrderTip" onClick={() => this.props.saveOrderHandler(row.id)}><BagCheck /></button>}
          <button type="button" className="icon-button ml-2" aria-label="Bestellung löschen" data-tip data-for="removeOrderTip" onClick={() => this.props.removeOrderHandler(row.id)}><Trash /></button>
          {hasOrderDetails && <button type="button" className="icon-button ml-2" aria-label="Bestellung kopieren" data-tip data-for="copyOrderTip" onClick={() => this.props.copyOrderHandler(row.id)}><Files /></button>}
          <ReactTooltip id="modifyOrderTip" place="top" effect="solid">Bestellung bearbeiten</ReactTooltip>
          {hasOrderDetails && <ReactTooltip id="saveOrderTip" place="top" effect="solid">Bestellung abschliessen</ReactTooltip>}
          <ReactTooltip id="removeOrderTip" place="top" effect="solid">Bestellung löschen</ReactTooltip>
          <ReactTooltip id="copyOrderTip" place="top" effect="solid">Bestellung kopieren</ReactTooltip>
        </div>
      );
    }
    else {
      return (
        <div className="d-flex justify-content-center">
          <button type="button" className="icon-button" aria-label="Bestellung kopieren" data-tip data-for="copyOrderTip" onClick={() => this.props.copyOrderHandler(row.id)}><Files /></button>
          <ReactTooltip id="copyOrderTip" place="top" effect="solid">Bestellung kopieren</ReactTooltip>
        </div>
      );
    }
  };

  headerFormatter = (_column: any, _columnIndex: any, _components: any) => {
    return (
      <div className="d-flex justify-content-center">
        <button type="button" className="icon-button" aria-label="Neue Bestellung erfassen" data-tip data-for="newOrderTip" onClick={() => this.props.addOrderHandler()}><BagPlus /></button>
        <ReactTooltip id="newOrderTip" place="top" effect="solid">Neue Bestellung erfassen</ReactTooltip>
      </div>
    );
  };

  dateFormatter = (cell: any, _row: any) => {
    return (
      <React.Fragment>
        {!!cell ? moment(cell).format('DD.MM.YYYY') : ""}
      </React.Fragment>
    );
  }

  stateFormatter = (cell: any, _row: any) => {
    return (
      <React.Fragment>
        {!!OrderState[cell] ? OrderState[cell] : "-"}
      </React.Fragment>
    );
  }

  columnEvents = {
    onClick: (_e: any, _column: any, _columnIndex: any, row: OrderItem, _rowIndex: any) => {
      this.props.selectOrderHandler(row.id);
    }
  }

  columns = [
    { dataField: 'orderDate', text: 'Bestelldatum', formatter: this.dateFormatter, events: this.columnEvents },
    { dataField: 'deliveryDate', text: 'Lieferdatum', headerClasses: 'd-none d-md-table-cell', classes: 'd-none d-md-table-cell', formatter: this.dateFormatter, events: this.columnEvents },
    { dataField: 'comment', text: 'Bemerkung', headerClasses: 'd-none d-md-table-cell', classes: 'd-none d-md-table-cell', events: this.columnEvents },
    { dataField: 'state', text: 'Status', formatter: this.stateFormatter, events: this.columnEvents },
    { dataField: '', text: '', isDummyField: true, headerStyle: () => { return { width: "110px" }; }, formatter: this.actionFormatter, headerFormatter: this.headerFormatter }
  ];

  render() {

    const selectRow: SelectRowProps<any> = {
      mode: 'radio',
      hideSelectColumn: true,
      clickToSelect: false,
      selected: this.state.selected,
      style: { background: 'lightgray' }
    };

    return (
      <DragAndDrop dropHandler={this.props.dropFileHandler}>
        <BootstrapTable
          hover
          classes="table-sm w-auto"
          keyField='id'
          data={this.state.orders}
          columns={this.columns}
          selectRow={selectRow}
          pagination={paginationFactory({ sizePerPage: 5, hidePageListOnlyOnePage: true, hideSizePerPage: true })}
          ref={(n) => (this.node = n)}
        />
      </DragAndDrop>
    );
  }
}
